import React, { useCallback, useEffect, useState } from "react";
import api, { brl, hojeISO } from "@/lib/api";
import { useRefreshListener } from "@/lib/refresh";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  Users, UserCheck, FileText, Banknote, FileCheck, TrendingUp, Wallet,
  CircleDollarSign, CalendarClock, AlertTriangle, CheckCheck, Loader2,
  Target,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";

import { RankingInadimplentes } from "@/components/RankingInadimplentes";
import { useIdentidade } from "@/lib/identidade";
import logoBanner from "@/assets/logo-dark-t.png";

const METRICS = [
  { key: "clientes_cadastrados", label: "Clientes Cadastrados", icon: Users, color: "text-slate-700" },
  { key: "clientes_ativos", label: "Clientes Ativos", icon: UserCheck, color: "text-emerald-600" },
  { key: "novas_solicitacoes", label: "Novas Solicitações", icon: FileText, color: "text-blue-600", link: "/solicitacoes" },
  { key: "emprestimos_ativos", label: "Empréstimos Ativos", icon: Banknote, color: "text-emerald-600", link: "/emprestimos" },
  { key: "contratos", label: "Contratos", icon: FileCheck, color: "text-slate-700", link: "/contratos" },
  { key: "emprestimos_finalizados", label: "Finalizados", icon: CheckCheck, color: "text-slate-500" },
];

const MONEY = [
  { key: "valor_total_emprestado", label: "Total Emprestado", icon: TrendingUp },
  { key: "valor_total_recebido", label: "Total Recebido", icon: Wallet, hint: "Dinheiro efetivamente recebido (sem estornos; desconto não conta)" },
  { key: "saldo_aberto", label: "Saldo em Aberto", icon: CircleDollarSign },
];

const ALERTS = [
  { key: "parcelas_vencendo_hoje", label: "Vencendo Hoje", icon: CalendarClock, cls: "bg-amber-50 border-amber-200 text-amber-700", link: "/parcelas" },
  { key: "parcelas_atrasadas", label: "Parcelas Atrasadas", icon: AlertTriangle, cls: "bg-red-50 border-red-200 text-red-700", link: "/parcelas" },
];

const PIE_COLORS = ["#10B981", "#94A3B8", "#EF4444", "#F59E0B"];
const CIANO = "#00C2E0";

const iso = (d) => hojeISO(d);
const PERIODOS = {
  hoje: () => { const t = iso(new Date()); return [t, t]; },
  "7d": () => { const t = new Date(); const d = new Date(); d.setDate(t.getDate() - 6); return [iso(d), iso(t)]; },
  mes: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)]; },
  mes_anterior: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))]; },
  ano: () => { const t = new Date(); return [`${t.getFullYear()}-01-01`, iso(t)]; },
};
const PERIODO_LABELS = { hoje: "Hoje", "7d": "7 dias", mes: "Mês atual", mes_anterior: "Mês anterior", ano: "Ano", custom: "Personalizado" };

const KPIS = [
  { key: "a_receber", label: "Total a receber", icon: CircleDollarSign, sub: "saldo de parcelas em aberto" },
  { key: "recebido_hoje", label: "Recebido hoje", icon: Wallet, sub: (m) => `${m.pagamentos_hoje} pagamento(s)` },
  { key: "recebido_periodo", label: "Recebido no período", icon: TrendingUp, sub: (m) => `${m.pagamentos_periodo} pagamento(s) · inclui juros/multa` },
  { key: "valor_atrasado", label: "Em atraso", icon: AlertTriangle, sub: (m) => `${m.parcelas_atrasadas} parcela(s) vencida(s)`, alert: true, link: "/parcelas" },
];

export default function Dashboard() {
  const { user } = useAuth();
  const ident = useIdentidade();
  const cor = ident.cores;
  const [m, setM] = useState(null);
  const [charts, setCharts] = useState(null);
  const [periodo, setPeriodo] = useState("mes");
  const [custom, setCustom] = useState(PERIODOS.mes());
  const navigate = useNavigate();
  const [de, ate] = periodo === "custom" ? custom : PERIODOS[periodo]();

  useEffect(() => {
    if (!de || !ate) return;
    api.get(`/dashboard/metrics?de=${de}&ate=${ate}`).then((r) => setM(r.data));
  }, [de, ate]);
  const [metaDia, setMetaDia] = useState(null);
  const loadAll = useCallback(() => {
    if (!de || !ate) return;
    api.get(`/dashboard/metrics?de=${de}&ate=${ate}`, { params: { _t: Date.now() } }).then((r) => setM(r.data));
    api.get("/collection/today", { params: { _t: Date.now() } }).then((r) => setMetaDia(r.data)).catch(() => setMetaDia(null));
  }, [de, ate]);
  useEffect(() => { loadAll(); }, [loadAll]);
  useRefreshListener(loadAll);
  useEffect(() => {
    const t = setInterval(() => document.visibilityState === "visible" && loadAll(), 60000);
    const v = () => document.visibilityState === "visible" && loadAll();
    document.addEventListener("visibilitychange", v); window.addEventListener("focus", v); window.addEventListener("pageshow", v);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", v); window.removeEventListener("focus", v); window.removeEventListener("pageshow", v); };
  }, [loadAll]);
  useEffect(() => { api.get("/dashboard/charts").then((r) => setCharts(r.data)); }, []);

  if (!m || !charts)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin gold-text" /></div>;

  return (
    <div className="space-y-6 fade-up" data-testid="dashboard-page">
      <div className="lg:hidden rounded-2xl px-4 py-3 flex items-center justify-center [contain:paint]" style={{ background: "var(--fundo-marca, #000617)" }} data-testid="dash-brand-banner">
        <img src={ident.src("imagem_entrada", logoBanner)} alt="TRT GESTÃO — Gerenciamento de Contratos" className="h-32 sm:h-36 w-auto max-w-full object-contain" />
      </div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Painel Administrativo</h1>
          <p className="text-slate-500 text-sm mt-1">Bem-vindo, <span className="sensitive font-semibold text-slate-700" data-testid="dash-welcome-name">{user?.name?.split(" ")[0]}</span> — visão geral da operação de crédito.</p>
        </div>
        <div className="flex flex-wrap items-center gap-1" data-testid="dash-period-filter">
          {Object.keys(PERIODO_LABELS).map((k) => (
            <button key={k} onClick={() => setPeriodo(k)} data-testid={`dash-period-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${periodo === k ? "bg-onyx text-white border-onyx" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
              {PERIODO_LABELS[k]}
            </button>
          ))}
          {periodo === "custom" && (
            <div className="flex items-center gap-1 ml-1">
              <input type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="h-8 rounded-md border px-2 text-xs" data-testid="dash-period-de" />
              <span className="text-xs text-slate-400">até</span>
              <input type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="h-8 rounded-md border px-2 text-xs" data-testid="dash-period-ate" />
            </div>
          )}
        </div>
      </div>

      {/* Meta do dia (fonte: /collection/today — pagamentos oficiais do dia, sem estornos; fuso America/Sao_Paulo) */}
      {metaDia && (
        <div className="trt-card p-4" data-testid="meta-diaria-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-semibold"><Target className="w-4 h-4 gold-text" /> Meta do dia ({metaDia.data ? metaDia.data.split("-").reverse().join("/") : "hoje"})</div>
            <div className="text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
              <span><span className="text-slate-500 text-xs">Recebido</span> <span className="font-extrabold mono" data-testid="meta-diaria-recebido">{brl(metaDia.recebido)}</span></span>
              <span><span className="text-slate-500 text-xs">Meta</span> <span className="font-bold mono" data-testid="meta-diaria-meta">{brl(metaDia.meta)}</span></span>
              <span><span className="text-slate-500 text-xs">Previsto hoje</span> <span className="mono" data-testid="meta-diaria-previsto">{brl(metaDia.previsto)}</span></span>
              {metaDia.meta > 0 && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${metaDia.percentual >= 100 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="meta-diaria-pct">{metaDia.percentual}%</span>}
            </div>
          </div>
          {metaDia.meta > 0 && <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${metaDia.percentual >= 100 ? "bg-emerald-500" : "bg-gold"}`} style={{ width: `${Math.min(metaDia.percentual, 100)}%` }} /></div>}
          <div className="text-[11px] text-slate-500 mt-1" data-testid="meta-diaria-origem">
            {metaDia.origem === "fixa" && `Meta fixa configurada em Configurações → Empresa (${brl(metaDia.meta_fixa)}). `}
            {metaDia.origem === "previsto" && (metaDia.previsto > 0 ? `Meta = previsto das parcelas que vencem hoje (${brl(metaDia.previsto)}); campo "meta diária" vazio. ` : `Sem meta fixa configurada e nenhuma parcela vence hoje: meta do dia = R$ 0,00 (o valor recebido vem de pagamentos de parcelas de outras datas). `)}
            {metaDia.origem === "zero" && "Meta diária configurada como zero: sem meta do dia (exibindo apenas o recebido). "}
            Recebido = {metaDia.pagamentos} pagamento(s) oficiais de hoje (principal + encargos, sem estornos){metaDia.descontos_hoje > 0 ? `; descontos concedidos ${brl(metaDia.descontos_hoje)} não contam` : ""}.
          </div>
        </div>
      )}

      {/* Meta do mês */}
      {m.meta_mensal > 0 && (
        <div className="trt-card p-4" data-testid="meta-mensal-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-bold flex items-center gap-2"><Target className="w-4 h-4 gold-text" /> Meta do mês</div>
            <div className="text-sm"><span className="font-extrabold mono" data-testid="meta-mensal-recebido">{brl(m.recebido_mes)}</span> <span className="text-slate-500">de {brl(m.meta_mensal)}</span> <span className={`ml-2 text-xs font-bold px-2 py-0.5 rounded-full ${m.meta_mensal_pct >= 100 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="meta-mensal-pct">{m.meta_mensal_pct}%</span></div>
          </div>
          <div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${m.meta_mensal_pct >= 100 ? "bg-emerald-500" : "bg-gold"}`} style={{ width: `${Math.min(m.meta_mensal_pct, 100)}%` }} data-testid="meta-mensal-barra" /></div>
          <div className="text-[11px] text-slate-500 mt-1">{m.meta_mensal_pct >= 100 ? "Meta atingida!" : `Faltam ${brl(Math.max(m.meta_mensal - m.recebido_mes, 0))} até o fim do mês`} • configurável em Configurações → Empresa</div>
        </div>
      )}

      {/* KPIs de leitura rápida */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPIS.map((k) => (
          <button key={k.key} onClick={() => k.link && navigate(k.link)} data-testid={`kpi-${k.key}`}
            className={`text-left rounded-xl p-4 border transition-shadow hover:shadow-md ${k.alert ? "bg-red-50 border-red-200" : "trt-card"} ${k.link ? "cursor-pointer" : "cursor-default"}`}>
            <k.icon className={`w-5 h-5 mb-2 ${k.alert ? "text-red-600" : "gold-text"}`} />
            <div className={`text-xl font-extrabold mono ${k.alert ? "text-red-700" : ""}`}>{brl(m[k.key])}</div>
            <div className="text-xs font-semibold text-slate-700 mt-0.5">{k.label}</div>
            <div className="text-[11px] text-slate-500">{typeof k.sub === "function" ? k.sub(m) : k.sub}</div>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Mini label="Contratos ativos" value={m.emprestimos_ativos} testid="kpi-contratos-ativos" onClick={() => navigate("/emprestimos")} />
        <Mini label="Contratos no período" value={`${m.contratos_periodo} • ${brl(m.emprestado_periodo)}`} testid="kpi-contratos-periodo" />
        <Mini label="Novos clientes no período" value={m.novos_clientes_periodo} testid="kpi-novos-clientes" />
        <Mini label="Acordos aguardando assinatura" value={m.acordos_pendentes} testid="kpi-acordos-pendentes" onClick={() => navigate("/acordos")} />
      </div>
      {m.clientes_sem_nascimento > 0 && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-1.5 cursor-pointer" onClick={() => navigate("/clientes")} data-testid="dash-sem-nascimento">{m.clientes_sem_nascimento} cliente(s) sem data de nascimento — pendência cadastral (não bloqueia o acesso ao Portal por CPF + senha). Clique para ver e atualizar.</p>}

      {/* Money highlight */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {MONEY.map((x) => (
          <div key={x.key} className="bg-onyx rounded-xl p-5 relative overflow-hidden gold-border [contain:paint]">
            <div className="absolute right-2 top-2 w-16 h-16 rounded-full bg-gold/10 blur-2xl" />
            <x.icon className="w-5 h-5 gold-text mb-3" />
            <div className="text-slate-400 text-xs uppercase tracking-wider">{x.label}</div>
            <div className="text-white text-2xl font-extrabold mono mt-1" data-testid={`metric-${x.key}`}>{brl(m[x.key])}</div>
            {x.hint && <div className="text-slate-500 text-[11px] mt-1" data-testid={`metric-hint-${x.key}`}>{x.hint}</div>}
          </div>
        ))}
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
        {ALERTS.map((a) => (
          <button key={a.key} onClick={() => navigate(a.link)} data-testid={`alert-${a.key}`}
            className={`flex items-center gap-4 rounded-xl p-4 border ${a.cls} hover:shadow-md transition-all text-left`}>
            <a.icon className="w-8 h-8" />
            <div>
              <div className="text-2xl font-extrabold">{m[a.key]}</div>
              <div className="text-xs font-medium">{a.label}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {METRICS.map((x) => (
          <div key={x.key} onClick={() => x.link && navigate(x.link)}
            className={`trt-card p-4 ${x.link ? "cursor-pointer" : ""}`} data-testid={`metric-${x.key}`}>
            <x.icon className={`w-5 h-5 ${x.color} mb-2`} />
            <div className="text-2xl font-extrabold tracking-tight">{m[x.key]}</div>
            <div className="text-xs text-slate-500 mt-0.5">{x.label}</div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="trt-card p-5">
          <h3 className="font-bold mb-4">Emprestado x Recebido por período</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.por_periodo}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="mes" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${v / 1000}k`} />
              <Tooltip formatter={(v) => brl(v)} />
              <Legend />
              <Bar dataKey="emprestado" name="Emprestado" fill={cor.primaria} radius={[4, 4, 0, 0]} />
              <Bar dataKey="recebido" name="Recebido" fill={cor.acento} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="trt-card p-5" data-testid="parcelas-status-card">
          <div className="flex items-start justify-between gap-2 flex-wrap mb-3"><h3 className="font-bold">Status das parcelas</h3><span className="text-[11px] text-slate-500" data-testid="parcelas-status-periodo">{charts.periodo_parcelas?.descricao}</span></div>
          <div className="grid grid-cols-2 gap-3">
            {charts.parcelas_status.map((e) => {
              const cor = { pago: "text-emerald-700 border-emerald-200 bg-emerald-50", pendente: "text-slate-800 border-slate-200 bg-slate-50", parcial: "text-amber-800 border-amber-200 bg-amber-50", vencido: "text-red-700 border-red-200 bg-red-50" }[e.filtro];
              return <button key={e.filtro} type="button" onClick={() => navigate(`/parcelas?status=${e.filtro}`)} className={`rounded-xl border p-3 text-left transition-transform active:scale-[0.98] hover:shadow ${cor}`} data-testid={`parcelas-status-${e.filtro}`}>
                <div className="text-2xl font-bold mono">{e.value}</div><div className="text-xs font-semibold">{e.name}</div></button>;
            })}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Vencidas incluem parciais já vencidas (horário de vencimento, fuso de Brasília). Toque para abrir a lista filtrada.</p>
        </div>

        <div className="trt-card p-5 lg:col-span-2">
          <h3 className="font-bold mb-4">Novos clientes por mês</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={charts.clientes_por_mes}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="mes" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="clientes" name="Novos clientes" stroke={cor.acento} strokeWidth={3} dot={{ fill: CIANO, stroke: cor.primaria, strokeWidth: 2, r: 4 }} activeDot={{ r: 6, fill: cor.primaria }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <RankingInadimplentes />
    </div>
  );
}

const Mini = ({ label, value, testid, onClick }) => (
  <div onClick={onClick} data-testid={testid} className={`trt-card p-3 ${onClick ? "cursor-pointer hover:shadow-md" : ""}`}>
    <div className="text-lg font-extrabold tracking-tight">{value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </div>
);
